import { filter, isEmpty, uniqBy } from 'lodash';
import momentTimezone from 'moment-timezone';

import * as types from './constants';

export const STEPS = {
  USERS: 'users',
  INCENTIVES: 'incentives',
  DETAILS: 'details'
};

export const INCENTIVES = {
  LINKS: 'links',
  GMV: 'gmv',
  BOOST_COMMISSION: 'boost_commission'
};

export const boostCommissionIncentiveTypes = {
  COMMISSION_RATE_MULTIPLIER: 'commission_rate_multiplier',
  MINIMUM_COMMISSION_RATE: 'minimum_commission_rate'
};

const defaultTimezone = 'America/Los_Angeles';

const getIncentive = (data, base_rate) => {
  if (!isEmpty(data.bonuses)) {
    return { incentive_type: INCENTIVES.LINKS, links_incentive: data.bonuses[0] };
  } else if (!isEmpty(data.gmv_modifiers)) {
    return { incentive_type: INCENTIVES.GMV, gmv_incentive: data.gmv_modifiers };
  } else if (!isEmpty(data.commission_rate_multipliers)) {
    return {
      incentive_type: INCENTIVES.BOOST_COMMISSION,
      boost_commission_incentive:
        {
          boost_type: boostCommissionIncentiveTypes.COMMISSION_RATE_MULTIPLIER,
          boosted_rate: data.commission_rate_multipliers[0].multiplier * base_rate,
          value: data.commission_rate_multipliers[0].multiplier,
          multiplier_value: data.commission_rate_multipliers[0].multiplier,
          minimum_rate_value: 0
        }
    };
  } else if (!isEmpty(data.minimum_commission_rates)) {
    return {
      incentive_type: INCENTIVES.BOOST_COMMISSION,
      boost_commission_incentive:
        {
          boost_type: boostCommissionIncentiveTypes.MINIMUM_COMMISSION_RATE,
          boosted_rate: data.minimum_commission_rates[0].minimum_rate_percentage,
          value: data.minimum_commission_rates[0].minimum_rate_percentage,
          minimum_rate_value: data.minimum_commission_rates[0].minimum_rate_percentage,
          multiplier_value: 1
        }
    };
  }
};

const initialState = {
  step: STEPS.USERS,
  base_rate: '',
  data: {
    id: null,
    name: '',
    description: '',
    date_start: '',
    date_start_tz: defaultTimezone,
    date_end: '',
    date_end_tz: defaultTimezone,
    image_url: '',
    incentive_type: '',
    boost_commission_incentive: {
      boost_type: '',
      value: 0,
      minimum_rate_value: 0,
      multiplier_value: 1,
      boosted_rate: 0
    },
    links_incentive: {
      reward: '',
      qualified_links_required: '',
      minimum_clicks: '',
      minimum_gmv: ''
    },
    gmv_incentive: [
      { minimum_gmv: '', reward: '' }
    ],
    users: []
  },
  errors: {
    active: false,
    details: {
      error: false,
      name: null,
      description: null,
      date_start: null,
      date_end: null,
      image_url: null
    },
    users: {
      error: null,
      value: []
    },
    incentives: {
      error: false,
      boost_commission_incentive: null,
      links_incentive: {
        reward: null,
        qualified_links_required: null,
        minimum_clicks: null,
        minimum_gmv: null
      },
      gmv_incentive: []
    },
    non_field_errors: null
  },
  unsavedChanges: false,
  connectedRowByUuid: {}
};

const actions = {
  // Campaign Edit Flow
  [types.CAMPAIGN_EDIT_FLOW_SET_STEP]: (state, { payload }) => {
    const newState = {
      ...state,
      step: payload
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_SET_BASE_RATE]: (state, { payload }) => {
    const newState = {
      ...state,
      base_rate: payload
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_SET_INIT]: (state, { payload }) => {
    const newState = {
      ...state,
      data: {
        ...state.data,
        id: payload.id,
        name: payload.name,
        description: payload.description,
        date_start: momentTimezone(payload.date_start).format(),
        date_start_tz: payload.date_start_tz || payload.date_start_timezone || state.data.date_start_tz,
        date_end: momentTimezone(payload.date_end).format(),
        date_end_tz: payload.date_end_tz || payload.date_end_timezone || state.data.date_end_tz,
        image_url: payload.image,
        ...getIncentive(payload, state.base_rate),
        users: payload.users
      }
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_SET_PROP]: (state, { payload }) => {
    const newState = {
      ...state,
      data: {
        ...state.data,
        [payload.prop]: payload.value
      },
      unsavedChanges: !payload.disableUnsavedChanges
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_SET_UNSAVED_CHANGES]: (state, { payload }) => {
    const newState = {
      ...state,
      unsavedChanges: payload
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_CLEAR]: () => {
    return { ...initialState };
  },

  [types.CAMPAIGN_EDIT_FLOW_SET_ERRORS]: (state, { payload }) => {
    const newErrors = {
      ...initialState.errors,
      details: {
        ...initialState.errors.details,
        ...(payload.name && { error: true, name: payload.name }),
        ...(payload.description && { error: true, description: payload.description }),
        ...(payload.date_start && { error: true, date_start: payload.date_start }),
        ...(payload.date_end && { error: true, date_end: payload.date_end }),
        ...(payload.image_url && { error: true, image_url: payload.image_url })
      },
      users: {
        ...(payload.users && { error: true, value: (typeof payload.users === 'string') ? payload.users : payload.users[0] })
      },
      incentives: {
        ...initialState.errors.incentives,
        ...(payload.bonuses && payload.bonuses[0] && { error: true, links_incentive: (typeof payload.bonuses === 'string') ? payload.bonuses : payload.bonuses[0] }),

        ...(payload.commission_rate_multipliers && ((typeof payload.commission_rate_multipliers === 'string')
          ? { error: true, boost_commission_incentive: payload.commission_rate_multipliers }
          : payload.commission_rate_multipliers[0] && payload.commission_rate_multipliers[0].multiplier && { error: true, boost_commission_incentive: payload.commission_rate_multipliers[0].multiplier })),

        ...(payload.minimum_commission_rates && ((typeof payload.minimum_commission_rates === 'string')
          ? { error: true, boost_commission_incentive: payload.minimum_commission_rates }
          : payload.minimum_commission_rates[0] && payload.minimum_commission_rates[0].minimum_rate_percentage && { error: true, boost_commission_incentive: payload.minimum_commission_rates[0].minimum_rate_percentage })),

        ...(payload.gmv_modifiers && { error: true, gmv_incentive: payload.gmv_modifiers })
      },
      non_field_errors: {
        ...(payload.users && { error: true, value: payload.non_field_errors })

      }
    };

    const newState = {
      ...state,
      errors: {
        ...newErrors,
        active: Object.values(newErrors).some(item => item.error === true)
      }
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_CLEAR_ERROR]: (state, { payload }) => {
    const newErrors = {
      ...state.errors,
      [payload]: initialState.errors[payload]
    };

    const newState = {
      ...state,
      errors: {
        ...newErrors,
        active: false
      }
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_CLEAR_ERRORS]: state => {
    const newState = {
      ...state,
      errors: {
        ...state.errors,
        active: false
      }
    };

    return { ...newState };
  },

  // Users Step
  [types.CAMPAIGN_EDIT_FLOW_ADD_USER]: (state, { payload }) => {
    const uuid = payload?.creator?.uuid;

    const newState = {
      ...state,
      connectedRowByUuid: uuid
        ? { ...state.connectedRowByUuid, [uuid]: payload }
        : state.connectedRowByUuid,
      data: {
        ...state.data,
        users: uniqBy([payload.creator, ...state.data.users], 'uuid')
      },
      unsavedChanges: true
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_ADD_USERS]: (state, { payload }) => {
    const newState = {
      ...state,
      data: {
        ...state.data,
        users: uniqBy([...payload, ...state.data.users], 'uuid')
      },
      unsavedChanges: true
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_REMOVE_USER]: (state, { payload }) => {
    const uuid = payload?.uuid;

    const nextMap = { ...state.connectedRowByUuid };
    if (uuid) delete nextMap[uuid];

    const newState = {
      ...state,
      connectedRowByUuid: nextMap,
      data: {
        ...state.data,
        users: filter(state.data.users, x => x.uuid !== payload.uuid)
      },
      unsavedChanges: true
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_REMOVE_ALL_USERS]: state => {
    const newState = {
      ...state,
      connectedRowByUuid: {},
      data: {
        ...state.data,
        users: []
      },
      unsavedChanges: true
    };

    return { ...newState };
  },

  // Incentives Step
  [types.CAMPAIGN_EDIT_FLOW_SET_INCENTIVE_TYPE]: (state, { payload }) => {
    const newState = {
      ...state,
      data: {
        ...state.data,
        incentive_type: payload
      },
      unsavedChanges: true
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_SET_LINKS_INCENTIVE]: (state, { payload }) => {
    const newState = {
      ...state,
      data: {
        ...state.data,
        links_incentive: {
          ...state.data.links_incentive,
          [payload.prop]: payload.value
        }
      },
      unsavedChanges: true
    };

    return { ...newState };
  },

  [types.CAMPAIGN_EDIT_FLOW_SET_BOOST_COMMISSION_INCENTIVE]: (state, { payload }) => {
    if (payload.prop === 'boost_type') {
      const newState = {
        ...state,
        data: {
          ...state.data,
          boost_commission_incentive: {
            ...state.data.boost_commission_incentive,
            boost_type: payload.value,
            value: payload.value === boostCommissionIncentiveTypes.MINIMUM_COMMISSION_RATE ? state.data.boost_commission_incentive.minimum_rate_value : state.data.boost_commission_incentive.multiplier_value,
            minimum_rate_value: state.data.boost_commission_incentive.boosted_rate,
            multiplier_value: state.base_rate ? (state.data.boost_commission_incentive.boosted_rate/state.base_rate) : 1
          }
        },
        unsavedChanges: true
      };

      return { ...newState };
    } else if (payload.prop === 'boosted_rate') {
      const newState = {
        ...state,
        data: {
          ...state.data,
          boost_commission_incentive: {
            ...state.data.boost_commission_incentive,
            boosted_rate: payload.value,
            minimum_rate_value: payload.value,
            multiplier_value: state.base_rate ? (payload.value/state.base_rate) : 1,
            value: state.data.boost_commission_incentive.boost_type === boostCommissionIncentiveTypes.MINIMUM_COMMISSION_RATE ? payload.value : (state.base_rate ? (payload.value/state.base_rate) : 0)
          }
        },
        unsavedChanges: true
      };

      return { ...newState };
    } else {
      const newState = {
        ...state,
        data: {
          ...state.data,
          boost_commission_incentive: {
            ...state.data.boost_commission_incentive,
            [payload.prop]: payload.value
          }
        },
        unsavedChanges: true
      };

      return { ...newState };
    }
  },

  [types.CAMPAIGN_EDIT_FLOW_SET_GMV_INCENTIVE]: (state, { payload }) => {
    const newState = {
      ...state,
      data: {
        ...state.data,
        gmv_incentive: payload
      },
      unsavedChanges: true
    };

    return { ...newState };
  }
};

export default (state = initialState, action) => {
  if (actions[action.type]) return actions[action.type](state, action);
  return state;
};